/**
 * Aperture Enterprises Logo
 * Double doors on an arched doorway, opening outward onto a warm-lit pathway.
 * Navy structure with warm gold light accents.
 *
 * Props:
 *   size — width in px (default 40). Height auto-scales at 9:10.
 *   mono — if true, uses only one color (for tight header spaces on light bg).
 *   color — override the primary color (default #0A2540 navy).
 */
export default function Logo({ size = 40, mono = false, color = "#0A2540", className = "" }) {
  const gold = "#C4A265";
  const lightWarm = "#F5E6C0";

  return (
    <svg
      viewBox="0 0 80 90"
      width={size}
      height={(size * 90) / 80}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Aperture Enterprises"
      role="img"
    >
      <defs>
        <linearGradient id="apertureWarmLight" x1="50%" y1="20%" x2="50%" y2="100%">
          <stop offset="0%" stopColor={lightWarm} stopOpacity="0.15" />
          <stop offset="45%" stopColor={lightWarm} stopOpacity="0.75" />
          <stop offset="100%" stopColor={gold} stopOpacity="1" />
        </linearGradient>
        <radialGradient id="apertureGlow" cx="50%" cy="90%" r="55%">
          <stop offset="0%" stopColor={lightWarm} stopOpacity="0.55" />
          <stop offset="60%" stopColor={gold} stopOpacity="0.15" />
          <stop offset="100%" stopColor={gold} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Outer soft glow spilling from the aperture */}
      {!mono && <ellipse cx="40" cy="80" rx="34" ry="10" fill="url(#apertureGlow)" />}

      {/* Warm-lit pathway (trapezoid receding into the doorway) */}
      {!mono && <polygon points="30,86 50,86 60,90 20,90" fill={gold} />}

      {/* Wedge of warm light coming through the open doors */}
      {!mono && <polygon points="35,22 45,22 52,86 28,86" fill="url(#apertureWarmLight)" />}

      {/* Arched doorway frame */}
      <path
        d="M 10 88 L 10 34 A 30 30 0 0 1 70 34 L 70 88"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />

      {/* Left door leaf — opening outward (inner edge angles toward the center-top) */}
      <path
        d="M 13 86 L 13 36 A 27 24 0 0 1 35 22 L 35 86 Z"
        fill={color}
      />

      {/* Right door leaf — mirrored */}
      <path
        d="M 45 22 A 27 24 0 0 1 67 36 L 67 86 L 45 86 Z"
        fill={color}
      />

      {/* Gold hairlines on each door (the seam of light along its edge) */}
      {!mono && (
        <>
          <line x1="34.5" y1="30" x2="34.5" y2="84" stroke={gold} strokeWidth="0.6" opacity="0.85" />
          <line x1="45.5" y1="30" x2="45.5" y2="84" stroke={gold} strokeWidth="0.6" opacity="0.85" />
          {/* subtle door panel line */}
          <line x1="23" y1="46" x2="23" y2="78" stroke={gold} strokeWidth="0.4" opacity="0.35" />
          <line x1="57" y1="46" x2="57" y2="78" stroke={gold} strokeWidth="0.4" opacity="0.35" />
          {/* handles */}
          <circle cx="30" cy="60" r="0.9" fill={gold} />
          <circle cx="50" cy="60" r="0.9" fill={gold} />
        </>
      )}

      {/* Gold cap-stone / keystone at the top of the arch */}
      {!mono && <circle cx="40" cy="7" r="1.6" fill={gold} />}
    </svg>
  );
}
